'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { instagramApi, instagramAccountsApi, type InstagramFeedSelection, type InstagramMediaItem, type InstagramInboxSession } from '@/lib/api';
import { ApiError } from '@/lib/upgrade-wall';

type PageState = { scope: string; items: InstagramMediaItem[] | null; cursor: string | null;
  loading: boolean; error: 'load' | 'page' | 'changed' | null };
const empty = (scope: string): PageState => ({ scope, items: null, cursor: null, loading: false, error: null });

/** Selection changes stay local. An explicit restart pins the latest draft without auto retrying a stale cursor. */
export function useInstagramMediaPage(businessId: string, selectedId: string | null = null, session?: InstagramInboxSession | null,
  shared?: { connectionId: string; targetBusinessId: string }) {
  const connectionId = shared?.connectionId, targetBusinessId = shared?.targetBusinessId;
  const scope = JSON.stringify([businessId, connectionId, targetBusinessId]);
  const [state, setState] = useState(() => empty(scope));
  const selected = useRef(selectedId); selected.current = selectedId;
  const currentSession = useRef(session); currentSession.current = session;
  const active = useRef<AbortController | null>(null);
  const load = useCallback(async (cursor: string | null) => {
    if (active.current) return;
    if (!businessId) { setState({ ...empty(scope), error: 'load' }); return; }
    const request = new AbortController(); active.current = request;
    setState(old => ({ ...(cursor ? old : empty(scope)), loading: true, error: null }));
    try {
      const page = connectionId && targetBusinessId
        ? await instagramAccountsApi.media(connectionId, targetBusinessId, { businessId, session: currentSession.current ?? null, signal: request.signal }, cursor ?? undefined, selected.current)
        : await instagramApi.getMediaPage(businessId, { cursor: cursor ?? undefined,
        selectedId: selected.current, signal: request.signal, session: currentSession.current });
      if (!Array.isArray(page.items) || !(page.nextCursor === null || typeof page.nextCursor === 'string') ||
        !(page.selected === null || page.selected && typeof page.selected.id === 'string')) throw new Error('Invalid media page');
      if (request.signal.aborted) return;
      setState(old => ({ scope, cursor: page.nextCursor, loading: false, error: null,
        items: [...new Map([...(cursor ? old.items ?? [] : []), ...page.items,
          ...(page.selected ? [page.selected] : [])].map(item => [item.id, item])).values()] }));
    } catch (error) {
      if (request.signal.aborted) return;
      const reset = error instanceof ApiError && [401, 403, 404, 409].includes(error.status);
      setState(old => ({ ...(reset || !cursor ? empty(scope) : old), loading: false,
        error: error instanceof ApiError && error.status === 409 ? 'changed' : cursor && !reset ? 'page' : 'load' }));
    } finally { if (active.current === request) active.current = null; }
  }, [businessId, connectionId, targetBusinessId, scope]);
  useEffect(() => {
    void load(null);
    return () => { active.current?.abort(); active.current = null; };
  }, [load]);
  // Hide the previous tenant synchronously, before effect cleanup and new I/O.
  const current = state.scope === scope ? state : empty(scope);
  return { items: current.items, loading: current.loading, error: current.error, hasMore: !!current.cursor,
    updateItem: (id: string, changes: Partial<Pick<InstagramMediaItem, 'showOnStorefront' | 'showOnMarketplace'>>) =>
      setState(old => old.scope !== scope ? old : { ...old,
        items: old.items?.map(item => item.id === id ? { ...item, ...changes } : item) ?? null }),
    updatePost: (result: InstagramFeedSelection) => setState(old => old.scope !== scope ? old : { ...old,
      items: old.items?.map(item => item.feedPostId === result.postId ? { ...item, feedVisibility: result.visibility,
        feedSelected: result.selected, feedBusinessIds: result.businessIds } : item) ?? null }),
    reload: () => void load(null), loadMore: () => { if (current.cursor) void load(current.cursor); } };
}
